class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


(() => {
  const CATEGORY_KEY = 'filter.p.m.custom.armadio_category';
  const F1_VALUE = 'Formula 1';
  const ORIGINAL_VALUE = 'Armadio Originals';
  const SIZE_LABELS = new Map([
    ['Double extra small (XXS)', '2XS'], ['Extra small (XS)', 'XS'], ['Small (S)', 'S'],
    ['Medium (M)', 'M'], ['Large (L)', 'L'], ['Extra large (XL)', 'XL'],
    ['Double extra large (XXL)', '2XL'], ['Triple extra large (XXXL)', '3XL']
  ]);
  const getMode = () => new URL(location.href).searchParams.get(CATEGORY_KEY);
  const setMode = (value) => {
    const url = new URL(location.href);
    url.searchParams.delete(CATEGORY_KEY);
    url.searchParams.set(CATEGORY_KEY, value);
    url.searchParams.delete('page');
    location.assign(url.toString());
  };
  const isShopAll = () => location.pathname === '/collections/all';
  const ensureDefaultMode = () => {
    if (!isShopAll() || getMode()) return false;
    setMode(F1_VALUE);
    return true;
  };
  const closestDisclosure = (element) => element.closest('.facets__disclosure-vertical, details.mobile-facets__details');
  const configureCategoryToggle = (root) => {
    const categoryInputs = [...root.querySelectorAll(`input[name="${CATEGORY_KEY}"]`)];
    if (!categoryInputs.length) return;
    const formulaInput = categoryInputs.find((input) => input.value === F1_VALUE);
    const originalInput = categoryInputs.find((input) => input.value === ORIGINAL_VALUE);
    if (originalInput) originalInput.closest('li, .mobile-facets__label')?.classList.add('armadio-filter-hidden');
    if (formulaInput && formulaInput.dataset.armadioModeBound !== 'true') {
      formulaInput.dataset.armadioModeBound = 'true';
      formulaInput.addEventListener('change', (event) => {
        event.stopImmediatePropagation();
        setMode(formulaInput.checked ? F1_VALUE : ORIGINAL_VALUE);
      }, true);
    }
    const mode = getMode();
    if (formulaInput) formulaInput.checked = mode !== ORIGINAL_VALUE;
  };
  const configureRelevantGroups = (root) => {
    const isFormula = getMode() !== ORIGINAL_VALUE;
    root.querySelectorAll('input').forEach((input) => {
      const name = input.name || '';
      const disclosure = closestDisclosure(input);
      if (!disclosure) return;
      if (name.includes('custom.f1_team')) disclosure.classList.toggle('armadio-filter-hidden', !isFormula);
      if (name.includes('shopify.fit') || name.includes('product_category')) disclosure.classList.add('armadio-filter-hidden');
    });
  };
  const shortenSizeLabels = (root) => {
    root.querySelectorAll('.facet-checkbox__text-label').forEach((label) => {
      const replacement = SIZE_LABELS.get(label.textContent.trim());
      if (replacement) label.textContent = replacement;
    });
  };
  const enhancePrice = (priceFields) => {
    if (priceFields.dataset.armadioRangeReady === 'true') return;
    const inputs = [...priceFields.querySelectorAll('input[type="number"]')];
    if (inputs.length < 2) return;
    const [fromInput, toInput] = inputs;
    const maximum = Number(toInput.max || fromInput.max || 0);
    if (!maximum) return;
    priceFields.dataset.armadioRangeReady = 'true';
    const range = document.createElement('div');
    range.className = 'armadio-price-range';
    range.innerHTML = `<input type="range" min="0" max="${maximum}" step="1" aria-label="Minimum price"><input type="range" min="0" max="${maximum}" step="1" aria-label="Maximum price">`;
    priceFields.parentNode.insertBefore(range, priceFields);
    const [minRange, maxRange] = range.querySelectorAll('input[type="range"]');
    minRange.value = fromInput.value || 0;
    maxRange.value = toInput.value || maximum;
    const paint = () => {
      const low = Number(minRange.value), high = Number(maxRange.value);
      range.style.setProperty('--armadio-range-start', `${(low / maximum) * 100}%`);
      range.style.setProperty('--armadio-range-end', `${(high / maximum) * 100}%`);
    };
    minRange.addEventListener('input', () => {
      if (Number(minRange.value) > Number(maxRange.value)) minRange.value = maxRange.value;
      fromInput.value = minRange.value === '0' ? '' : minRange.value; paint();
    });
    maxRange.addEventListener('input', () => {
      if (Number(maxRange.value) < Number(minRange.value)) maxRange.value = minRange.value;
      toInput.value = maxRange.value === String(maximum) ? '' : maxRange.value; paint();
    });
    [minRange, maxRange].forEach((control) => control.addEventListener('change', () => {
      const target = control === minRange ? fromInput : toInput;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }));
    paint();
  };
  const enhance = (root = document) => {
    configureCategoryToggle(root); configureRelevantGroups(root); shortenSizeLabels(root);
    root.querySelectorAll('.facets__price').forEach(enhancePrice);
  };
  if (ensureDefaultMode()) return;
  enhance();
  new MutationObserver(() => enhance()).observe(document.documentElement, { childList: true, subtree: true });
})();


// Armadio theme compatibility: Dawn uses text price inputs and taxonomy category handles.
(() => {
  const applyArmadioCompat = () => {
    document.querySelectorAll('input[name="filter.p.t.category"], input[name*="shopify.fit"]').forEach((input) => {
      input.closest('.facets__disclosure-vertical, details.mobile-facets__details')?.classList.add('armadio-filter-hidden');
    });

    document.querySelectorAll('.facets__price').forEach((fields) => {
      if (fields.parentElement?.querySelector('.armadio-price-range')) return;
      const fromInput = fields.querySelector('input[name="filter.v.price.gte"]');
      const toInput = fields.querySelector('input[name="filter.v.price.lte"]');
      if (!fromInput || !toInput) return;
      const maximum = Number(toInput.getAttribute('max') || fromInput.getAttribute('max') || 0);
      if (!maximum) return;

      const range = document.createElement('div');
      range.className = 'armadio-price-range';
      range.innerHTML = `<input type="range" min="0" max="${maximum}" step="1" aria-label="Minimum price"><input type="range" min="0" max="${maximum}" step="1" aria-label="Maximum price">`;
      fields.parentNode.insertBefore(range, fields);
      const [minimum, maximumControl] = range.querySelectorAll('input[type="range"]');
      minimum.value = fromInput.value || 0;
      maximumControl.value = toInput.value || maximum;
      const paint = () => {
        range.style.setProperty('--armadio-range-start', `${(Number(minimum.value) / maximum) * 100}%`);
        range.style.setProperty('--armadio-range-end', `${(Number(maximumControl.value) / maximum) * 100}%`);
      };
      minimum.addEventListener('input', () => {
        if (+minimum.value > +maximumControl.value) minimum.value = maximumControl.value;
        fromInput.value = minimum.value === '0' ? '' : minimum.value;
        paint();
      });
      maximumControl.addEventListener('input', () => {
        if (+maximumControl.value < +minimum.value) maximumControl.value = minimum.value;
        toInput.value = maximumControl.value === String(maximum) ? '' : maximumControl.value;
        paint();
      });
      [minimum, maximumControl].forEach((control, index) => control.addEventListener('change', () => {
        (index ? toInput : fromInput).dispatchEvent(new Event('change', { bubbles: true }));
      }));
      paint();
    });
  };
  applyArmadioCompat();
  new MutationObserver(applyArmadioCompat).observe(document.documentElement, { childList: true, subtree: true });
})();


// Armadio price slider fallback for localized Shopify data-max values.
(() => {
  const addLocalizedPriceSliders = () => document.querySelectorAll('.facets__price').forEach((fields) => {
    if (fields.parentElement?.querySelector('.armadio-price-range')) return;
    const fromInput = fields.querySelector('input[name="filter.v.price.gte"]');
    const toInput = fields.querySelector('input[name="filter.v.price.lte"]');
    if (!fromInput || !toInput) return;
    const maximum = Number(String(toInput.dataset.max || fromInput.dataset.max || '').replace(/,/g, ''));
    if (!maximum) return;
    const range = document.createElement('div');
    range.className = 'armadio-price-range';
    range.innerHTML = `<input type="range" min="0" max="${maximum}" step="1" aria-label="Minimum price"><input type="range" min="0" max="${maximum}" step="1" aria-label="Maximum price">`;
    fields.parentNode.insertBefore(range, fields);
    const [minimum, maximumControl] = range.querySelectorAll('input[type="range"]');
    minimum.value = String(fromInput.value || '0').replace(/,/g, '');
    maximumControl.value = String(toInput.value || maximum).replace(/,/g, '');
    const paint = () => {
      range.style.setProperty('--armadio-range-start', `${(+minimum.value / maximum) * 100}%`);
      range.style.setProperty('--armadio-range-end', `${(+maximumControl.value / maximum) * 100}%`);
    };
    minimum.addEventListener('input', () => {
      if (+minimum.value > +maximumControl.value) minimum.value = maximumControl.value;
      fromInput.value = minimum.value === '0' ? '' : minimum.value; paint();
    });
    maximumControl.addEventListener('input', () => {
      if (+maximumControl.value < +minimum.value) maximumControl.value = minimum.value;
      toInput.value = maximumControl.value === String(maximum) ? '' : maximumControl.value; paint();
    });
    [minimum, maximumControl].forEach((control, index) => control.addEventListener('change', () => (index ? toInput : fromInput).dispatchEvent(new Event('change', {bubbles:true}))));
    paint();
  });
  addLocalizedPriceSliders();
  new MutationObserver(addLocalizedPriceSliders).observe(document.documentElement, {childList:true,subtree:true});
})();
